/* eslint-disable react-hooks/exhaustive-deps */
// TimeKeep.jsx

import React, { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { KeepAwake } from "@capacitor-community/keep-awake";
import { Preferences } from "@capacitor/preferences";
import {
  EllipsisVertical,
  Moon,
  Sun,
  BarChart3,
  Save,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Monitor,
  Laptop,
  User,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * ===== Helpers =====
 */
export const toDateKeyLocal = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const formatTimeMMSScc = (ms) => {
  const totalCentiseconds = Math.floor(ms / 10);
  const cs = totalCentiseconds % 100;
  const totalSeconds = Math.floor(totalCentiseconds / 100);
  const s = totalSeconds % 60;
  const m = Math.floor(totalSeconds / 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(
    cs
  ).padStart(2, "0")}`;
};

export const formatReadable = (ms) => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours} hr ${remainingMinutes} min${seconds > 0 ? " " + seconds + " sec" : ""}`.trim();
  } else if (minutes > 0) {
    return `${minutes} min${seconds > 0 ? " " + seconds + " sec" : ""}`.trim();
  } else {
    return `${seconds} sec`;
  }
};

export const getMonthDays = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days = Array(firstDay)
    .fill(null)
    .concat(Array.from({ length: daysInMonth }, (_, i) => i + 1));
  return days;
};

/**
 * ===== Main Component =====
 */
export default function StopwatchReplica() {
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [name, setName] = useState("");
  const [showPrompt, setShowPrompt] = useState(false);
  const [tempName, setTempName] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    if (saved !== null) return JSON.parse(saved);
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    );
  });

  const [showStatistics, setShowStatistics] = useState(false);
  const [showAbout, setShowAbout] = useState(false);

  const [sessions, setSessions] = useState(() => {
    const saved = localStorage.getItem("sessions");
    return saved ? JSON.parse(saved) : [];
  });

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [todayKey, setTodayKey] = useState("");

  const [keepAwake, setKeepAwake] = useState(false);
  const wakeLockRef = useRef(null);
  const visHandlerRef = useRef(null);

  const startRef = useRef(null);
  const frameRef = useRef(null);

  /**
   * Timer ticker
   */
  useEffect(() => {
    const tick = (time) => {
      setElapsed(time - startRef.current);
      frameRef.current = requestAnimationFrame(tick);
    };

    if (running) {
      if (startRef.current === null) {
        startRef.current = performance.now() - elapsed;
      }
      if (!frameRef.current) {
        frameRef.current = requestAnimationFrame(tick);
      }
    } else {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      startRef.current = null;
    }

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [running, elapsed]);

  /**
   * Init "today"
   */
  useEffect(() => {
    const now = new Date();
    const localDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const key = toDateKeyLocal(localDate);
    setCurrentMonth(localDate);
    setTodayKey(key);
    setSelectedDay(null);
  }, []);

  /**
   * Load KeepAwake setting
   */
  useEffect(() => {
    const loadKeepAwake = async () => {
      try {
        let initial = false;
        if (Capacitor.isNativePlatform()) {
          const { value } = await Preferences.get({ key: "keepAwake" });
          initial = value === "true";
        } else {
          const saved = localStorage.getItem("keepAwake");
          initial = saved ? JSON.parse(saved) : false;
        }
        setKeepAwake(initial);
      } catch {
        setKeepAwake(false);
      }
    };
    loadKeepAwake();
  }, []);

  /**
   * Apply KeepAwake
   */
  useEffect(() => {
    let cancelled = false;

    const acquireBrowserWakeLock = async () => {
      try {
        if (!("wakeLock" in navigator)) return;
        if (wakeLockRef.current) {
          try {
            await wakeLockRef.current.release();
          } catch {}
          wakeLockRef.current = null;
        }
        const lock = await navigator.wakeLock.request("screen");
        if (cancelled) {
          try {
            await lock.release();
          } catch {}
          return;
        }
        wakeLockRef.current = lock;
        lock.addEventListener("release", () => {});
        const onVis = async () => {
          if (
            document.visibilityState === "visible" &&
            keepAwake &&
            !Capacitor.isNativePlatform()
          ) {
            try {
              const newLock = await navigator.wakeLock.request("screen");
              wakeLockRef.current = newLock;
            } catch {}
          }
        };
        visHandlerRef.current = onVis;
        document.addEventListener("visibilitychange", onVis);
      } catch {}
    };

    const releaseBrowserWakeLock = async () => {
      try {
        if (wakeLockRef.current) {
          await wakeLockRef.current.release();
        }
      } catch {}
      wakeLockRef.current = null;
      if (visHandlerRef.current) {
        document.removeEventListener("visibilitychange", visHandlerRef.current);
        visHandlerRef.current = null;
      }
    };

    const apply = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          if (keepAwake) {
            await KeepAwake.keepAwake();
          } else {
            await KeepAwake.allowSleep();
          }
          await Preferences.set({
            key: "keepAwake",
            value: keepAwake ? "true" : "false",
          });
        } catch {
          if (keepAwake) await acquireBrowserWakeLock();
          else await releaseBrowserWakeLock();
          localStorage.setItem("keepAwake", JSON.stringify(keepAwake));
        }
      } else {
        if (keepAwake) await acquireBrowserWakeLock();
        else await releaseBrowserWakeLock();
        localStorage.setItem("keepAwake", JSON.stringify(keepAwake));
      }
    };

    apply();

    return () => {
      cancelled = true;
      if (!Capacitor.isNativePlatform()) {
        if (visHandlerRef.current) {
          document.removeEventListener(
            "visibilitychange",
            visHandlerRef.current
          );
          visHandlerRef.current = null;
        }
      }
    };
  }, [keepAwake]);

  /**
   * Persist settings
   */
  useEffect(() => {
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem("sessions", JSON.stringify(sessions));
  }, [sessions]);

  /**
   * Controls
   */
  const handleStart = () => {
    if (!running && !name) {
      setShowPrompt(true);
      return;
    }
    setRunning((prev) => !prev);
  };

  const reset = () => {
    setRunning(false);
    setElapsed(0);
    startRef.current = null;
  };

  const saveSession = () => {
    const formatted = formatReadable(elapsed);
    const now = new Date();
    const dateKey = toDateKeyLocal(
      new Date(now.getFullYear(), now.getMonth(), now.getDate())
    );
    setSessions((prev) => [...prev, { name, time: formatted, date: dateKey }]);
    reset();
  };

  const confirmName = () => {
    if (tempName.trim() !== "") {
      setName(tempName.trim());
      setTempName("");
      setShowPrompt(false);
      // Small delay to prevent animation glitch
      setTimeout(() => {
        setRunning(true);
      }, 100);
    }
  };

  const goToToday = () => {
    const now = new Date();
    const localDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const key = toDateKeyLocal(localDate);
    setSelectedDay(null);
    setCurrentMonth(localDate);
    setTodayKey(key);
  };

  return (
    <div
      className={`min-h-screen ${
        darkMode ? "bg-black text-white" : "bg-white text-black"
      } flex flex-col relative transition-colors duration-300`}
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
      }}
    >
      {/* ===== Main Screen ===== */}
      {!showStatistics && !showAbout && (
        <>
          <div className="h-5" />

          {/* Header */}
          <div className="flex items-center justify-between px-4 pt-3 pb-2 relative">
            <motion.h1 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent"
            >
              TimeKeep
            </motion.h1>
            <div className="relative">
              <motion.button 
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setMenuOpen(!menuOpen)} 
                aria-label="Open menu"
                className="relative z-10"
              >
                <EllipsisVertical className={darkMode ? "text-gray-400" : "text-gray-600"} />
              </motion.button>
              <AnimatePresence>
                {menuOpen && (
                  <>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-10"
                      onClick={() => setMenuOpen(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.8, y: -10 }}
                      transition={{ type: "spring", damping: 20, stiffness: 300 }}
                      className={`absolute right-0 mt-2 w-56 ${
                        darkMode ? "bg-neutral-900" : "bg-white"
                      } ${
                        darkMode ? "border-neutral-700" : "border-gray-200"
                      } border rounded-2xl shadow-2xl z-20 p-2 overflow-hidden`}
                    >
                      {/* Keep Screen Awake */}
                      <motion.button
                        whileHover={{ x: 4 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => {
                          setKeepAwake((prev) => !prev);
                          setMenuOpen(false);
                        }}
                        className={`flex items-center gap-3 w-full text-left px-4 py-3 rounded-xl transition-colors ${
                          darkMode ? "hover:bg-neutral-800" : "hover:bg-gray-100"
                        }`}
                      >
                        <motion.div
                          animate={{
                            rotate: keepAwake ? [0, 360] : 0,
                            scale: keepAwake ? [1, 1.2, 1] : 1,
                          }}
                          transition={{ duration: 0.5 }}
                        >
                          <Monitor
                            className={
                              keepAwake 
                                ? darkMode ? "text-blue-400" : "text-blue-600"
                                : darkMode ? "text-gray-500" : "text-gray-400"
                            }
                          />
                        </motion.div>
                        <span className={keepAwake ? (darkMode ? "text-blue-400" : "text-blue-600") : ""}>
                          {keepAwake ? "Screen Awake ON" : "Screen Awake OFF"}
                        </span>
                      </motion.button>

                      {/* Theme toggle */}
                      <motion.button
                        whileHover={{ x: 4 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => {
                          setDarkMode((prev) => !prev);
                          setMenuOpen(false);
                        }}
                        className={`flex items-center gap-3 w-full text-left px-4 py-3 rounded-xl transition-colors ${
                          darkMode ? "hover:bg-neutral-800" : "hover:bg-gray-100"
                        }`}
                      >
                        <motion.div
                          animate={{
                            rotate: darkMode ? 180 : 0,
                            scale: [1, 1.2, 1],
                          }}
                          transition={{ duration: 0.5 }}
                        >
                          {darkMode ? <Sun className="text-yellow-400" /> : <Moon className="text-indigo-600" />}
                        </motion.div>
                        <span>{darkMode ? "Light Mode" : "Dark Mode"}</span>
                      </motion.button>

                      {/* View Statistics */}
                      <motion.button
                        whileHover={{ x: 4 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => {
                          setShowStatistics(true);
                          setMenuOpen(false);
                        }}
                        className={`flex items-center gap-3 w-full text-left px-4 py-3 rounded-xl transition-colors ${
                          darkMode ? "hover:bg-neutral-800" : "hover:bg-gray-100"
                        }`}
                      >
                        <BarChart3 className={darkMode ? "text-green-400" : "text-green-600"} />
                        <span>View Statistics</span>
                      </motion.button>

                      {/* About Developer */}
                      <motion.button
                        whileHover={{ x: 4 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => {
                          setShowAbout(true);
                          setMenuOpen(false);
                        }}
                        className={`flex items-center gap-3 w-full text-left px-4 py-3 rounded-xl transition-colors ${
                          darkMode ? "hover:bg-neutral-800" : "hover:bg-gray-100"
                        }`}
                      >
                        <span className="relative w-5 h-5 inline-flex items-center justify-center">
                          <Laptop className={`w-5 h-5 absolute ${darkMode ? "text-purple-400" : "text-purple-600"}`} />
                          <User className={`w-3 h-3 absolute -top-1 ${darkMode ? "text-purple-400" : "text-purple-600"}`} />
                        </span>
                        <span>About Developer</span>
                      </motion.button>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Session name */}
          <AnimatePresence>
            {name && (
              <motion.div
                initial={{ opacity: 0, y: -20, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.9 }}
                transition={{ type: "spring", damping: 15 }}
                className="text-center px-6 mb-4"
              >
                <div className={`inline-block ${
                  darkMode 
                    ? "bg-gradient-to-r from-blue-500/20 to-purple-500/20 border-blue-500/30" 
                    : "bg-gradient-to-r from-blue-100 to-purple-100 border-blue-300"
                } backdrop-blur-sm border rounded-2xl px-6 py-3 shadow-lg`}>
                  <p className={`text-[32px] sm:text-[40px] font-bold ${
                    darkMode 
                      ? "bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent"
                      : "bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
                  }`}>
                    {name}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Timer */}
          <div className="flex-1 flex items-center justify-center px-4">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", damping: 12 }}
              className="relative"
            >
              {/* Glow effect */}
              <AnimatePresence>
                {running && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{
                      scale: [1, 1.3, 1],
                      opacity: [0.2, 0.5, 0.2],
                    }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 2.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="absolute inset-0 bg-gradient-to-r from-blue-500/40 to-purple-500/40 blur-3xl rounded-full"
                  />
                )}
              </AnimatePresence>
              
              <motion.div
                animate={running ? { scale: [1, 1.01, 1] } : {}}
                transition={running ? { duration: 1, repeat: Infinity } : {}}
                className="font-semibold relative z-10"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                <span className="text-[64px] sm:text-[88px] md:text-[110px] leading-none select-none">
                  {elapsed === 0 && !running
                    ? "00:00.00"
                    : formatTimeMMSScc(elapsed)}
                </span>
              </motion.div>
            </motion.div>
          </div>

          {/* Controls */}
          <div className="pb-24 flex items-center justify-center gap-4 px-4">
            <AnimatePresence mode="wait">
              {elapsed > 0 && (
                <motion.button
                  initial={{ scale: 0, opacity: 0, x: -20 }}
                  animate={{ scale: 1, opacity: 1, x: 0 }}
                  exit={{ scale: 0, opacity: 0, x: -20 }}
                  transition={{ type: "spring", damping: 15 }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={reset}
                  className={`px-6 py-4 rounded-2xl font-medium shadow-lg border transition-all min-w-[110px] ${
                    darkMode
                      ? "bg-neutral-900 text-gray-300 border-neutral-700 hover:bg-neutral-800"
                      : "bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200"
                  }`}
                >
                  Reset
                </motion.button>
              )}
            </AnimatePresence>

            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleStart}
              aria-label={running ? "Pause" : "Start"}
              className={`px-10 py-4 rounded-2xl font-bold text-lg shadow-2xl transition-all ${
                running
                  ? darkMode
                    ? "bg-orange-600 hover:bg-orange-500 text-white"
                    : "bg-orange-500 hover:bg-orange-600 text-white"
                  : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white"
              }`}
            >
              {running ? "Pause" : "Start"}
            </motion.button>

            <AnimatePresence mode="wait">
              {!running && elapsed > 0 && (
                <motion.button
                  initial={{ scale: 0, opacity: 0, x: 20 }}
                  animate={{ scale: 1, opacity: 1, x: 0 }}
                  exit={{ scale: 0, opacity: 0, x: 20 }}
                  transition={{ type: "spring", damping: 15 }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={saveSession}
                  className={`px-6 py-4 rounded-2xl font-medium shadow-lg border transition-all flex items-center gap-2 min-w-[110px] ${
                    darkMode
                      ? "bg-green-600 hover:bg-green-500 text-white border-green-700"
                      : "bg-green-500 hover:bg-green-600 text-white border-green-600"
                  }`}
                >
                  <Save size={18} />
                  <span>Save</span>
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          {/* Name Prompt */}
          <AnimatePresence>
            {showPrompt && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-30 p-4"
                onClick={(e) => {
                  if (e.target === e.currentTarget) setShowPrompt(false);
                }}
              >
                <motion.div
                  initial={{ scale: 0.8, opacity: 0, y: 50 }}
                  animate={{ scale: 1, opacity: 1, y: 0 }}
                  exit={{ scale: 0.8, opacity: 0, y: 50 }}
                  transition={{ type: "spring", damping: 20 }}
                  className={`${
                    darkMode ? "bg-neutral-950 border-neutral-800" : "bg-white border-gray-200"
                  } border p-6 rounded-3xl w-full max-w-sm flex flex-col gap-5 shadow-2xl`}
                >
                  <h2 className={`text-xl font-semibold text-center ${
                    darkMode ? "text-gray-200" : "text-gray-800"
                  }`}>
                    Session Name
                  </h2>
                  <input
                    type="text"
                    value={tempName}
                    onChange={(e) => setTempName(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && confirmName()}
                    autoFocus
                    className={`px-4 py-3 rounded-2xl ${
                      darkMode 
                        ? "bg-neutral-900 text-white border-neutral-700" 
                        : "bg-gray-50 text-gray-900 border-gray-300"
                    } border focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500 transition-all`}
                    placeholder="E.g. Maths, Physics"
                  />
                  <div className="flex justify-end gap-3">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowPrompt(false)}
                      className={`px-5 py-2 rounded-2xl ${
                        darkMode 
                          ? "bg-neutral-800 text-gray-300 hover:bg-neutral-700"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      } transition-colors`}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={confirmName}
                      className="px-5 py-2 rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-500 hover:to-purple-500 transition-all"
                    >
                      Start
                    </motion.button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}

      {/* ===== About Overlay ===== */}
      <AnimatePresence>
        {showAbout && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-30 p-4"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: "spring", damping: 20 }}
              className={`${
                darkMode ? "bg-neutral-950 border-neutral-800" : "bg-white border-gray-200"
              } border p-6 rounded-3xl w-full max-w-md flex flex-col gap-5 shadow-2xl text-center`}
            >
              <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                About Developer
              </h2>
              <p className={darkMode ? "text-gray-300" : "text-gray-700"}>
                Hi, I'm Divyansh Goyal.
              </p>
              
              <div className="flex flex-col gap-3 mt-2">
                <motion.a
                  whileHover={{ scale: 1.03, x: 4 }}
                  whileTap={{ scale: 0.97 }}
                  href="https://divyanshgoyal.me"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center justify-center gap-3 px-5 py-3 rounded-2xl ${
                    darkMode 
                      ? "bg-neutral-900 text-gray-300 hover:bg-neutral-800" 
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  } transition-all`}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  <span>Website</span>
                </motion.a>
                
                <motion.a
                  whileHover={{ scale: 1.03, x: 4 }}
                  whileTap={{ scale: 0.97 }}
                  href="https://github.com/divyanshgoyal1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center justify-center gap-3 px-5 py-3 rounded-2xl ${
                    darkMode 
                      ? "bg-neutral-900 text-gray-300 hover:bg-neutral-800" 
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  } transition-all`}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.17 6.839 9.49.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.463-1.11-1.463-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.167 22 16.418 22 12c0-5.523-4.477-10-10-10z"/>
                  </svg>
                  <span>GitHub</span>
                </motion.a>
                
                <motion.a
                  whileHover={{ scale: 1.03, x: 4 }}
                  whileTap={{ scale: 0.97 }}
                  href="https://instagram.com/divyanshgoyal.me"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center justify-center gap-3 px-5 py-3 rounded-2xl ${
                    darkMode 
                      ? "bg-neutral-900 text-gray-300 hover:bg-neutral-800" 
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  } transition-all`}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  <span>Instagram</span>
                </motion.a>
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAbout(false)}
                className="px-5 py-2 rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-500 hover:to-purple-500 mt-2 transition-all"
              >
                Close
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== Statistics Page ===== */}
      <AnimatePresence>
        {showStatistics && (
          <motion.div
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{ type: "spring", damping: 20 }}
            className="flex flex-col min-h-screen absolute inset-0 bg-inherit z-20"
          >
            {/* Header */}
            <div className={`flex items-center justify-between p-4 border-b ${
              darkMode ? "border-neutral-800" : "border-gray-200"
            }`}>
              <motion.button
                whileHover={{ scale: 1.05, x: -4 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setShowStatistics(false);
                  goToToday();
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-colors ${
                  darkMode ? "hover:bg-neutral-800" : "hover:bg-gray-100"
                }`}
              >
                <ArrowLeft size={20} />
                <span>Back</span>
              </motion.button>
              <h2 className="text-xl font-bold flex-1 text-center -ml-16">
                Statistics
              </h2>
            </div>

            <div className="p-4 flex-1 overflow-y-auto">
              {/* Month switcher */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex justify-between items-center max-w-lg mx-auto mb-6 p-4 rounded-2xl ${
                  darkMode ? "bg-neutral-900/50" : "bg-gray-100"
                }`}
              >
                <motion.button
                  whileHover={{ scale: 1.1, x: -2 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() =>
                    setCurrentMonth(
                      new Date(
                        currentMonth.getFullYear(),
                        currentMonth.getMonth() - 1
                      )
                    )
                  }
                  className={`p-2 rounded-xl transition-colors ${
                    darkMode ? "hover:bg-neutral-800" : "hover:bg-gray-200"
                  }`}
                >
                  <ChevronLeft size={24} />
                </motion.button>
                <h3 className="font-bold text-xl">
                  {currentMonth.toLocaleString("default", { month: "long" })}{" "}
                  {currentMonth.getFullYear()}
                </h3>
                <motion.button
                  whileHover={{ scale: 1.1, x: 2 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() =>
                    setCurrentMonth(
                      new Date(
                        currentMonth.getFullYear(),
                        currentMonth.getMonth() + 1
                      )
                    )
                  }
                  className={`p-2 rounded-xl transition-colors ${
                    darkMode ? "hover:bg-neutral-800" : "hover:bg-gray-200"
                  }`}
                >
                  <ChevronRight size={24} />
                </motion.button>
              </motion.div>

              {/* Calendar */}
              <div className="grid grid-cols-7 gap-2 text-center max-w-lg mx-auto mb-8">
                {"SMTWTFS".split("").map((d, i) => (
                  <motion.div 
                    key={d}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className={`text-sm font-bold py-2 ${
                      darkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    {d}
                  </motion.div>
                ))}
                {getMonthDays(currentMonth).map((day, i) => {
                  if (!day) return <div key={i}></div>;

                  const year = currentMonth.getFullYear();
                  const month = currentMonth.getMonth();
                  const localDate = new Date(year, month, day);
                  const key = toDateKeyLocal(localDate);
                  const isToday = key === todayKey;
                  const isSelected = key === selectedDay;
                  const hasSessions = sessions.some((s) => s.date === key);

                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.02 }}
                      whileHover={{ scale: 1.15, y: -4 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setSelectedDay(key)}
                      className={`h-12 sm:h-14 flex items-center justify-center rounded-xl cursor-pointer font-semibold text-sm sm:text-base transition-all ${
                        hasSessions
                          ? isSelected
                            ? "bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-xl scale-110"
                            : "bg-gradient-to-br from-green-600 to-emerald-700 text-white hover:shadow-lg"
                          : isSelected
                          ? darkMode
                            ? "bg-neutral-700 text-white ring-2 ring-blue-500"
                            : "bg-gray-300 text-gray-800 ring-2 ring-blue-500"
                          : darkMode
                          ? "bg-neutral-800 text-gray-400 hover:bg-neutral-700"
                          : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                      } ${isToday ? "ring-2 ring-blue-400" : ""}`}
                    >
                      {day}
                    </motion.div>
                  );
                })}
              </div>

              {/* Sessions list */}
              <div className="mt-8 max-w-xl mx-auto">
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center justify-between mb-4"
                >
                  <h3 className="font-bold text-2xl">
                    {selectedDay
                      ? new Date(selectedDay + "T00:00:00").toLocaleDateString(
                          "default",
                          { month: "long", day: "numeric", year: "numeric" }
                        )
                      : "Sessions"}
                  </h3>
                  {selectedDay &&
                    sessions.some((s) => s.date === selectedDay) && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          darkMode
                            ? "bg-blue-600/20 text-blue-400"
                            : "bg-blue-100 text-blue-600"
                        }`}
                      >
                        {sessions.filter((s) => s.date === selectedDay).length}{" "}
                        session{sessions.filter((s) => s.date === selectedDay).length !== 1 ? "s" : ""}
                      </motion.div>
                    )}
                </motion.div>

                {!selectedDay ||
                !sessions.some((s) => s.date === selectedDay) ? (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`text-center p-12 rounded-2xl ${
                      darkMode
                        ? "bg-neutral-900/50 border border-neutral-800"
                        : "bg-gray-50 border border-gray-200"
                    }`}
                  >
                    <motion.div
                      animate={{ rotate: [0, 10, -10, 0] }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                    >
                      <BarChart3 size={48} className={`mx-auto mb-4 ${
                        darkMode ? "text-gray-600" : "text-gray-400"
                      }`} />
                    </motion.div>
                    <p className={`text-lg ${
                      darkMode ? "text-gray-500" : "text-gray-600"
                    }`}>
                      {selectedDay
                        ? "No sessions saved for this day"
                        : "Select a day to view sessions"}
                    </p>
                  </motion.div>
                ) : (
                  <motion.ul
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-3"
                  >
                    {sessions
                      .filter((s) => s.date === selectedDay)
                      .map((s, idx) => (
                        <motion.li
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1, type: "spring", damping: 20 }}
                          whileHover={{ scale: 1.03, x: 4 }}
                          className={`p-5 rounded-2xl shadow-md transition-all ${
                            darkMode
                              ? "bg-gradient-to-br from-neutral-900 to-neutral-800 border border-neutral-700 hover:border-neutral-600"
                              : "bg-white border border-gray-200 hover:border-gray-300 hover:shadow-lg"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-bold text-lg mb-1">{s.name}</p>
                              <p className={`text-sm flex items-center gap-2 ${
                                darkMode ? "text-gray-400" : "text-gray-600"
                              }`}>
                                <motion.span 
                                  animate={{ scale: [1, 1.3, 1] }}
                                  transition={{ duration: 1.5, repeat: Infinity }}
                                  className="inline-block w-2 h-2 bg-green-500 rounded-full"
                                ></motion.span>
                                {s.time}
                              </p>
                            </div>
                            <div className={`px-4 py-2 rounded-xl font-mono text-sm ${
                              darkMode
                                ? "bg-neutral-800 text-blue-400"
                                : "bg-blue-50 text-blue-600"
                            }`}>
                              ⏱️ {s.time.split(" ")[0]}
                            </div>
                          </div>
                        </motion.li>
                      ))}
                  </motion.ul>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}